/**
 * ContractorAccessTab — F11 T11.7a FE.
 *
 * Zakładka „Dostępy firm" w karcie kontrahenta. Lista wszystkich aktywnych firm
 * tenanta z checkboxem „Udostępnij". Home company jest zaznaczona ON disabled
 * (z badge „Macierzysta") — nie da się jej odpiąć.
 *
 * Per-row toggle: POST /company-access (share) lub DELETE /:companyId (revoke).
 * Dodatkowo: button „Udostępnij wszystkim" → POST /share-all (mass share dla
 * wszystkich firm bez aktywnego CCA naraz).
 *
 * Backend:
 *   - GET /api/companies (list)
 *   - GET /api/contractors/:id/company-access (lista CCA + home)
 *   - POST /api/contractors/:id/company-access { companyId } (share single)
 *   - DELETE /api/contractors/:id/company-access/:companyId (revoke)
 *   - POST /api/contractors/:id/share-all (mass share)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Skeleton } from '@/components/Skeleton';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';
import { runConflictText } from '@/lib/record-procedures/run-texts';
import { extractRunConflict } from '@/lib/record-procedures/runs-api';

interface Company {
  id: string;
  legalName: string;
}

interface AccessEntry {
  companyId: string;
  companyName: string;
  isOwner: boolean;
}

interface AccessResponse {
  access: AccessEntry[];
  homeCompanyId: string;
}

interface ContractorAccessTabProps {
  contractorId: string;
}

/** Zakładka nie zna napisów przycisków SQL z układu — komunikat blokady użyje nazwy procedury. */
const NO_BUTTON_LABELS: ReadonlyMap<string, string> = new Map();

export function ContractorAccessTab({ contractorId }: ContractorAccessTabProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const companiesQ = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      // Backend `/api/companies` już filtruje active (`withActive(companies)`) — nie ma
      // potrzeby filtrować po `gcrecord` w FE. Wcześniej był tu filtr `c.gcrecord === null`
      // który zwracał `[]` bo backend nie zwraca pola gcrecord w SELECT (undefined !== null).
      // Picker dostępu — pełna lista firm; od migracji na silnik list `/companies` jest
      // paginowane (domyślny `limit=50`), więc jawnie `limit=200` (górny clamp silnika).
      // ZNANY DŁUG: tenant z >200 firmami cicho urwie nadmiar (firm per tenant zwykle mało).
      const res = await api.get<{ companies: Company[] }>('/companies?limit=200');
      return res.data.companies;
    },
  });

  const accessQ = useQuery({
    queryKey: ['contractor-access', contractorId],
    queryFn: async () => {
      const res = await api.get<AccessResponse>(`/contractors/${contractorId}/company-access`);
      return res.data;
    },
  });

  function invalidateAccess() {
    void queryClient.invalidateQueries({
      queryKey: ['contractor-access', contractorId],
    });
  }

  const shareMutation = useMutation({
    mutationFn: async (companyId: string) => {
      await api.post(`/contractors/${contractorId}/company-access`, { companyId });
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.access.toast.shared'),
      });
      invalidateAccess();
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.access.toast.shareError'),
        description: extractErrorCode(err),
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (companyId: string) => {
      await api.delete(`/contractors/${contractorId}/company-access/${companyId}`);
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.access.toast.revoked'),
      });
      invalidateAccess();
    },
    onError: (err) => {
      const code = (isAxiosError(err) ? err.response?.data?.error : null) as string | null;
      if (code === 'IS_OWNER') {
        toast.show({
          variant: 'error',
          title: t('tenant.contractors.access.toast.cannotRevokeOwner'),
        });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.access.toast.revokeError'),
        description: extractErrorCode(err),
      });
    },
  });

  const [transferTarget, setTransferTarget] = useState<{ id: string; name: string } | null>(null);

  const transferMutation = useMutation({
    mutationFn: async (newCompanyId: string) => {
      await api.post(`/contractors/${contractorId}/transfer-ownership`, { newCompanyId });
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.access.toast.transferred'),
      });
      invalidateAccess();
      // Inwalidujemy też contractor żeby nagłówek karty zaktualizował home company.
      void queryClient.invalidateQueries({ queryKey: ['contractor', contractorId] });
      // Firma rekordu zmieniła się, a od niej zależy cały obieg kontrahenta (decyzja właściciela
      // 2026-10-08): prawa startu / anulowania z listingu spraw rekordu, szczegóły spraw oraz listy
      // „Moje czekające" (kolumna, filtr i grupowanie „Firma") — więc, jak inne mutacje obiegu w
      // panelu, unieważniamy cały obszar `processes`, nie pojedynczy klucz.
      void queryClient.invalidateQueries({ queryKey: ['processes'] });
      setTransferTarget(null);
    },
    onError: (err) => {
      const code = (isAxiosError(err) ? err.response?.data?.error : null) as string | null;
      if (code === 'NO_ACCESS_TO_TARGET_COMPANY') {
        toast.show({
          variant: 'error',
          title: t('tenant.contractors.access.toast.transferNoAccess'),
        });
        return;
      }
      if (code === 'PERMISSION_DENIED') {
        toast.show({
          variant: 'error',
          title: t('tenant.contractors.access.toast.transferNoPermission'),
        });
        return;
      }
      if (code === 'SHORT_NAME_CONFLICT_IN_TARGET_COMPANY') {
        toast.show({
          variant: 'error',
          title: t('tenant.contractors.access.toast.transferShortNameConflict'),
        });
        return;
      }
      // Zmiana firmy odrzucona na czas zadania przycisku SQL na tym rekordzie — które zadanie blokuje.
      const runConflict = extractRunConflict(err);
      if (runConflict?.code === 'RECORD_LOCKED_BY_RUN') {
        toast.show({
          variant: 'error',
          title: runConflictText(runConflict, NO_BUTTON_LABELS, t, i18n.language),
        });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.access.toast.transferError'),
        description: extractErrorCode(err),
      });
    },
  });

  const shareAllMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ granted: number }>(`/contractors/${contractorId}/share-all`, {});
      return res.data;
    },
    onSuccess: (data) => {
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.access.toast.shareAll', {
          count: data.granted,
        }),
      });
      invalidateAccess();
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.access.toast.shareAllError'),
        description: extractErrorCode(err),
      });
    },
  });

  const isLoading = companiesQ.isLoading || accessQ.isLoading;
  const isError = companiesQ.isError || accessQ.isError;

  if (isError) {
    return <p className="text-sm text-red-600 py-4">{t('tenant.contractors.access.loadError')}</p>;
  }

  if (isLoading || !companiesQ.data || !accessQ.data) {
    return (
      <div className="flex flex-col gap-2 py-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={40} />
        ))}
      </div>
    );
  }

  const accessByCompany = new Map(accessQ.data.access.map((a) => [a.companyId, a]));
  const homeCompanyId = accessQ.data.homeCompanyId;
  const companies = companiesQ.data;

  // Liczba firm bez aktywnego CCA + nie home → kandydaci do share-all.
  const candidatesForShareAll = companies.filter(
    (c) => c.id !== homeCompanyId && !accessByCompany.has(c.id),
  ).length;

  function handleToggle(companyId: string, currentlyShared: boolean) {
    if (currentlyShared) {
      revokeMutation.mutate(companyId);
    } else {
      shareMutation.mutate(companyId);
    }
  }

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-text-primary">
            {t('tenant.contractors.access.title')}
          </h3>
          <p className="text-xs text-text-tertiary">{t('tenant.contractors.access.subtitle')}</p>
        </div>
        <Button
          onClick={() => shareAllMutation.mutate()}
          loading={shareAllMutation.isPending}
          disabled={candidatesForShareAll === 0}
          data-testid="contractor-access-share-all-btn"
        >
          {t('tenant.contractors.access.shareAllBtn', { count: candidatesForShareAll })}
        </Button>
      </div>

      {companies.length === 0 ? (
        <p className="text-sm text-text-tertiary py-4 text-center">
          {t('tenant.contractors.access.empty')}
        </p>
      ) : (
        <div
          className="border border-border-secondary rounded-lg divide-y divide-border-secondary"
          data-testid="contractor-access-list"
        >
          {companies.map((company) => {
            const entry = accessByCompany.get(company.id);
            const isHome = company.id === homeCompanyId;
            const isShared = entry !== undefined;
            return (
              <div
                key={company.id}
                className="flex items-center gap-3 px-3 py-2 hover:bg-bg-secondary"
                data-testid={`contractor-access-row-${company.id}`}
              >
                <Checkbox
                  checked={isShared}
                  disabled={isHome || shareMutation.isPending || revokeMutation.isPending}
                  onChange={() => handleToggle(company.id, isShared)}
                  aria-label={t('tenant.contractors.access.toggleAria', {
                    name: company.legalName,
                  })}
                  data-testid={`contractor-access-toggle-${company.id}`}
                  // Przełącznik zapisuje się od razu — to nie jest „niezapisana zmiana" formularza,
                  // w którym karta jest osadzona (zabezpieczenie zamknięcia okna).
                  data-no-dirty
                />
                <span className="flex-1 text-sm text-text-primary">{company.legalName}</span>
                {isHome ? (
                  <Badge color="accent">{t('tenant.contractors.access.homeBadge')}</Badge>
                ) : (
                  <button
                    type="button"
                    onClick={() => setTransferTarget({ id: company.id, name: company.legalName })}
                    disabled={transferMutation.isPending}
                    className="text-xs text-accent-600 hover:underline disabled:opacity-50"
                    data-testid={`contractor-access-transfer-${company.id}`}
                  >
                    {t('tenant.contractors.access.transferBtn')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={transferTarget !== null}
        title={t('tenant.contractors.access.transferConfirm.title')}
        message={
          transferTarget
            ? t('tenant.contractors.access.transferConfirm.message', {
                name: transferTarget.name,
              })
            : ''
        }
        confirmLabel={t('tenant.contractors.access.transferBtn')}
        onConfirm={() => transferTarget && transferMutation.mutate(transferTarget.id)}
        onClose={() => setTransferTarget(null)}
      />
    </div>
  );
}
